// routes
import { useAtom } from "jotai";
import { useEffect, useState } from "react";
import { Toaster } from "react-hot-toast";
import ScrollToTop from "./components/ScrollToTop";
import { StorageConstants } from "./constants/AppConstants";
import {
  configuration,
  infoDialog,
  merchantDetailsState,
  responsive,
} from "./global/jotaiState";
import { generateUniquieId } from "./helper/generateUniqueId";
import ObjectStorage from "./modules/ObjectStorage";
import Router, { ProRouter } from "./routes";
import AuthService from "./services/authService";

import "react-responsive-carousel/lib/styles/carousel.min.css";
import ThemeSettings from "./components/settings";
import "react-simple-keyboard/build/css/index.css";
import InfoDialog from "./components/InfoDialog";
import ThemeProvider from "./theme";
import { useAppConfiguration } from "./global/configs/appConfig";
import { useMerchant } from "./global/configs/merchant";
import { SettingsProvider } from "./hooks/contexts/settingsContext";
import { getLatLong } from "./utils/getCoords";
import isEmpty from "lodash/isEmpty";
import { useTableDetails } from "./global/tables/details";
import { fetchTableDetails } from "./api/tableQR/table";
import NavigateSetter from "./components/NavigateSetter";
import OfflineFallback from "./components/OfflineFallback";
import { get, isUndefined } from "lodash";
import { usePlanType } from "./hooks/usePlanType";

function App() {
  const deviceId = AuthService.getDeviceId();
  const setWindowSize = useAtom(responsive)[1];
  const [infoDialogState, setInfoDialogState] = useAtom(infoDialog);
  const [appConfig, setAppConfig] = useAtom(configuration);
  const [merchantDetails, setMerchantDetails] = useAtom(merchantDetailsState);
  const { resetAppConfig } = useAppConfiguration();
  const [storeOffline, setStoreOffline] = useState(false);
  const { resetMerchantDetails } = useMerchant();
  const { updateDetails } = useTableDetails();
  const { isProPlan, isLitePlan, isPaidAccount, isUltraPlan } = usePlanType();
  const isMenuDisplay =
    get(
      JSON.parse(sessionStorage.getItem("merchantDetails")),
      "menuDisplay",
      "false",
    ) === "true"
      ? true
      : false;
  const isOnlineScanQR = !isUndefined(appConfig?.onlineScanQrOrders)
    ? appConfig?.onlineScanQrOrders
    : !isUndefined(appConfig?.disableScanQrOrders)
      ? !appConfig?.disableScanQrOrders
      : true;
  const isOnlineTableOrders = !isUndefined(appConfig?.onlineTableOrders)
    ? appConfig?.onlineTableOrders
    : !isUndefined(appConfig?.disableTableOrders)
      ? !appConfig?.disableTableOrders
      : true;
  const getTableDetails = async (tableId) => {
    updateDetails(tableId);
  };
  const mutate = async () => {
    resetMerchantDetails();
    await resetAppConfig();
    const merchantDetails = JSON.parse(
      sessionStorage.getItem("merchantDetails"),
    );
    const isTableQr = !isEmpty(merchantDetails?.tableId);
    if (isTableQr) {
      await getTableDetails(merchantDetails?.tableId);
    }
  };
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    mutate();
    window.addEventListener("resize", handleResize);

    if (!deviceId)
      ObjectStorage.setItem(StorageConstants.DEVICE_UNIQUE_ID, {
        id: generateUniquieId(),
      });

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (merchantDetails?.tableId) {
      setStoreOffline(!isOnlineTableOrders);
    } else {
      setStoreOffline(!isOnlineScanQR);
    }
  }, [isOnlineScanQR, isOnlineTableOrders, merchantDetails]);

  if ((storeOffline && !isUltraPlan) || isLitePlan || !isPaidAccount)
    return <OfflineFallback />;
  return (
    <>
      <SettingsProvider>
        <ThemeProvider>
          <ThemeSettings>
            <Toaster />
            <ScrollToTop />
            {isProPlan || (storeOffline && isUltraPlan) ? (
              <ProRouter />
            ) : (
              <Router isMenuDisplay={isMenuDisplay} />
            )}
            <NavigateSetter />
            <InfoDialog
              onClose={() => {
                setInfoDialogState({ ...infoDialogState, show: false });
              }}
              title={infoDialogState.title}
              desc={infoDialogState.desc}
              content={infoDialogState.content}
              show={infoDialogState.show}
            />
          </ThemeSettings>
        </ThemeProvider>
      </SettingsProvider>
    </>
  );
}

export default App;
